export const AUTH_STORAGE_KEYS = {
    accessToken: 'access_token',
    accessTokenTimestamp: 'access_token_timestamp',
    impersonatorAccessToken: 'impersonator_access_token',

    hostApp: 'host-app',
    hostSupport: 'host-support',
    hostUrl: 'host-url',

    hostLayoutFavicon: 'host-layout-favicon',
    hostLayoutLogo: 'host-layout-logo',
    hostLayoutTheme: 'host-layout-theme',
    hostLayoutWatermark: 'host-layout-watermark',

    // hostLayoutFaviconTmp: 'host-layout-favicon-tmp',
    // hostLayoutLogoTmp: 'host-layout-logo-tmp',
    // hostLayoutThemeTmp: 'host-layout-theme-tmp',
    // hostLayoutWatermarkTmp: 'host-layout-watermark-tmp',

    user: 'user',
    organizations: 'organizations',
    access: 'access',
    selectedOrganization: 'selected-organization',
    selectedBranch: 'selected-branch',
    impersonatorSelectedOrganization: 'impersonator_selected_organization',
    impersonatorSelectedBranch: 'impersonator_selected_branch'
} as const;

export type AuthStorageKey = (typeof AUTH_STORAGE_KEYS)[keyof typeof AUTH_STORAGE_KEYS];

export const AUTH_SESSION_RESET_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.accessToken,
    AUTH_STORAGE_KEYS.accessTokenTimestamp,
    AUTH_STORAGE_KEYS.user,
    AUTH_STORAGE_KEYS.organizations,
    AUTH_STORAGE_KEYS.access,
    AUTH_STORAGE_KEYS.impersonatorAccessToken,
    AUTH_STORAGE_KEYS.selectedOrganization,
    AUTH_STORAGE_KEYS.selectedBranch
] as const;

export const AUTH_REFRESH_FAILURE_RESET_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.accessToken,
    AUTH_STORAGE_KEYS.accessTokenTimestamp,
    AUTH_STORAGE_KEYS.user,
    AUTH_STORAGE_KEYS.organizations,
    AUTH_STORAGE_KEYS.access,
    AUTH_STORAGE_KEYS.impersonatorAccessToken
] as const;

export const AUTH_IMPERSONATION_SELECTION_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.impersonatorSelectedOrganization,
    AUTH_STORAGE_KEYS.impersonatorSelectedBranch
] as const;

function hasStorage(): boolean {
    return typeof localStorage !== 'undefined';
}

export const AuthStorage = {
    get(key: AuthStorageKey): string {
        if (!hasStorage()) {
            return '';
        }

        return localStorage.getItem(key) ?? '';
    },

    set(key: AuthStorageKey, value: string): void {
        if (!hasStorage()) {
            return;
        }

        localStorage.setItem(key, value);
    },

    remove(key: AuthStorageKey): void {
        if (!hasStorage()) {
            return;
        }

        localStorage.removeItem(key);
    },

    removeMany(keys: readonly AuthStorageKey[]): void {
        if (!hasStorage()) {
            return;
        }

        keys.forEach((key) => localStorage.removeItem(key));
    },

    setJson(key: AuthStorageKey, value: unknown): void {
        if (!hasStorage()) {
            return;
        }

        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // Ignore storage serialization errors.
        }
    },

    getJson<T>(key: AuthStorageKey): T | null {
        if (!hasStorage()) {
            return null;
        }

        const raw = localStorage.getItem(key);
        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw) as T;
        } catch {
            localStorage.removeItem(key);
            return null;
        }
    }
} as const;
